import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import {
  KeyArea,
  KeyphraseRow,
} from '../../core/interfaces/KeyphraseExtraction';
import { NgModel, FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { Ripple } from 'primeng/ripple';
import { ToolbarModule } from 'primeng/toolbar';
import { Dialog } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { BaseHttpService } from '../../core/services/base-http.service';
import { WebSocketService } from '../../core/services/web-socket.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  map,
  of,
  tap,
  withLatestFrom,
} from 'rxjs';
import { StateService } from '../../shared/services/state.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ErrorComponent } from '../error/error.component';
@Component({
  selector: 'app-keyphrase-extraction',
  standalone: true,
  imports: [
    TableModule,
    ToastModule,
    CommonModule,
    TagModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    ToolbarModule,
    Ripple,
    Dialog,
    TextareaModule,
    ConfirmDialog,
    ProgressSpinnerModule,
    ErrorComponent
  ],
  templateUrl: './keyphrase-extraction.component.html',
  styleUrl: './keyphrase-extraction.component.scss',
  providers: [MessageService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeyphraseExtractionComponent implements OnInit {

  // Dependency Injections
  private _messageService = inject(MessageService);
  private _confirmationService = inject(ConfirmationService);
  private _webSocketService = inject(WebSocketService);
  private _stateService = inject(StateService);
  private _baseHttpService = inject(BaseHttpService);

  //signal state slices
  awaitingRefresh = this._stateService.awaitingKeyphraseRefresh;
  keyphrasextractionTableIncoming =
    this._stateService.keyphraseExtractionTableIncoming;
  isLoading = this._stateService.isLoading;

  // Private Properties
  private cloneKeyphraseRow: { [s: string]: KeyphraseRow } = {};
  private justSavedSubject = new BehaviorSubject<boolean>(false);
  private justSaved$ = this.justSavedSubject.asObservable();
  refresh$ = this._stateService.keyphraseWebSocketNotification$.pipe(
    withLatestFrom(this.justSaved$),
    map(([wsNotification, justSaved]) => {
      if (justSaved) {
        this.justSavedSubject.next(false);
        return false;
      }
      return wsNotification;
    }),
    tap((flag) => {
      
      if (flag) {
        this._stateService.promptKeyphraseRefresh();
      } else {
        this.clearTableEditTags();
        this._stateService.confirmKeyphraseRefresh();
      }
    })
  );

  tableData$ = this._stateService.getTables$.pipe(
    tap((data) => console.log('table data: ', data))
  );

  // Angular Lifecycle Hooks
  ngOnInit(): void {}

  // Constructor
  constructor() {
    effect(() => {
      console.log(
        'current table data: ',
        this.keyphrasextractionTableIncoming()
      );
    });
  }

  // Public Properties
  keyphraseData!: KeyphraseRow[];
  keyPhraseDict = [
    { label: 'Competitors', value: 'Competitors' },
    { label: 'Account', value: 'Account' },
    { label: 'Products', value: 'Products' },
    { label: 'Billing', value: 'Billing' },
    { label: 'Churn', value: 'Churn' },
  ];
  addNewRowDialog: WritableSignal<boolean> = signal(false);
  submitted: WritableSignal<boolean> = signal(false);
  selectValue = signal<{ label: string; value: string }>({
    label: '',
    value: '',
  });
  selectedKeyPhrases = signal<KeyphraseRow[]>([]);
  changedRows = signal<string[]>([]);
  deletedRows = signal<string[]>([]);
  addedRows = signal<string[]>([]);

  disableSave = computed(() => {
    return (
      !(this.changedRows().length > 0) &&
      !(this.deletedRows().length > 0) &&
      !(this.addedRows().length > 0)
     || this.awaitingRefresh());
  })
  selectedKeyphraseRow: any;
  selectedKeyphraseRows: any;
  keyphraseRow: WritableSignal<KeyphraseRow> = signal({
    id: "0",
    key_area: '',
    key_area_en_definition: '',
    key_area_gr_definition: '',
    key_phrase_en: '',
    key_phrase_gr: '',
  });
  @ViewChild('text2') text2!: ElementRef;
  @ViewChild('text3') text3!: ElementRef;
  @ViewChild('text4') text4!: ElementRef;
  @ViewChild('text5') text5!: ElementRef;

  pendingSave = computed(
    () =>
      (this.changedRows().length > 0 || this.deletedRows().length > 0 || this.addedRows().length > 0) &&
      !this.awaitingRefresh()
  );
  selectValueDefinitionGr = computed(
    () =>
      this.keyphrasextractionTableIncoming().find(
        (KeyphraseRow) => KeyphraseRow.key_area == this.selectValue().value
      )?.key_area_gr_definition
  );
  selectValueDefinitionEn = computed(
    () =>
      this.keyphrasextractionTableIncoming().find(
        (KeyphraseRow) => KeyphraseRow.key_area == this.selectValue().value
      )?.key_area_en_definition
  );

  /**
   * @summary Refreshes the table data by triggering a new data fetch.
   * @description Sets the refresh flag to true temporarily to trigger a new data fetch
   * from the WebSocket stream. After 1 second, resets the flag to false to allow future refreshes.
   *
   * @see tableData$ for the data stream that handles the refresh
   * @see shouldRefresh$ for the BehaviorSubject that controls refresh state
   */
  refreshTable() {
    this._stateService.getFreshData();
    this.clearTableEditTags();
  }

  /**
   * @summary Opens the dialog for adding a new keyphrase row.
   * @description Resets the keyphrase form to its initial state and displays
   * the dialog modal for adding a new keyphrase entry.
   *
   * @see saveRow() for the method that handles saving the new entry
   * @see hideDialog() for closing the dialog
   */
  openNew() {
    this.keyphraseRow.set({
      id: "0",
      key_area: '',
      key_area_en_definition: '',
      key_area_gr_definition: '',
      key_phrase_en: '',
      key_phrase_gr: '',
    });
    this.submitted.set(false);
    this.addNewRowDialog.set(true);
  }

  /**
   * @summary Hides the add/edit dialog and resets form state.
   * @description Closes the dialog modal and resets the submitted flag
   * to prepare for the next interaction.
   */
  hideDialog() {
    this.addNewRowDialog.set(false);
    this.submitted.set(false);
  }


  /**
   * @summary Calculates the next available ID for a new keyphrase row.
   * @description Finds the smallest positive integer not currently used as an ID
   * in the keyphrase table to ensure unique IDs for new entries.
   *
   * @returns {number} The smallest available ID number
   */
  calculateRandomId(): string {
    return crypto.randomUUID();
  }


  /**
   * @summary Saves a new keyphrase row to the table.
   * @description Validates and saves the new keyphrase entry, updating the state
   * and displaying appropriate success/error messages.
   *
   * @see calculateRandomId() for ID generation
   * @see addedRows for tracking newly added rows
   * @see _stateService.addKeyphrase() for state management
   */
  saveRow() {
    this.keyphraseRow.update((keyphrase) => ({
      id: this.calculateRandomId(),
      key_area: this.selectValue().value,
      key_area_gr_definition: this.text2.nativeElement.value,
      key_area_en_definition: this.text3.nativeElement.value,
      key_phrase_gr: this.text4.nativeElement.value,
      key_phrase_en: this.text5.nativeElement.value,
    }));

    this.submitted.set(true);

    if (
      this.keyphraseRow().key_area &&
      this.keyphraseRow().key_phrase_gr &&
      this.keyphraseRow().key_area_gr_definition &&
      this.keyphraseRow().key_phrase_en
    ) {
      this._stateService.addKeyphrase(this.keyphraseRow());
      this.addedRows.update(rows => [...rows, this.keyphraseRow().key_phrase_gr]);

      this.addNewRowDialog.set(false);

      this.keyphraseRow.set({
        id: "0",
        key_area: '',
        key_area_en_definition: '',
        key_area_gr_definition: '',
        key_phrase_en: '',
        key_phrase_gr: '',
      });

      this._messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: 'New row created',
        life: 3000,
      });
    }
  }

  /**
   * @summary Saves all pending changes to the table.
   * @description Shows a confirmation dialog and, if confirmed, sends all changes
   * to the server via WebSocket, updates the local state, and resets change tracking.
   *
   * @see changedRows for tracking edited rows
   * @see deletedRows for tracking deleted rows
   * @see _webSocketService.sendKeyphraseTableUpdate() for server sync
   */
  saveChanges() {

    this._confirmationService.confirm({
      message: 'Are you sure you want to save all changed cells?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Save',
        severity: 'success',
      },
      accept: () => {
        this._baseHttpService.sendKeyphraseUpdate({
          table_name: 'nrg_key_phrases_definitions',
          rows: this.keyphrasextractionTableIncoming(),
        }).subscribe(
          data => {
            this.justSavedSubject.next(true); 
            this.clearTableEditTags();
            this._messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Table was updated',
            });
          }
        );
        // this._webSocketService.sendTopicClassificationUpdate({ items: [] });
      },
    });
  }

  clearTableEditTags(){
    this.deletedRows.update(() => []);
    this.changedRows.update(() => []);
    this.addedRows.update(() => []);
  }

  /**
   * @summary Handles the deletion of selected keyphrase rows.
   * @description Shows a confirmation dialog for deleting selected rows,
   * updates the state if confirmed, and manages the addedRows array to
   * maintain consistency with newly added rows.
   *
   * @see selectedKeyPhrases for the rows to be deleted
   * @see deletedRows for tracking pending deletions
   */
  deleteSelectedKeyPhrases() {
    const rowsToDelete = this.selectedKeyPhrases().filter(
      keyphrase => !this.addedRows().includes(keyphrase.key_phrase_gr!)
    );
  
    if (rowsToDelete.length > 0) {
      this._confirmationService.confirm({
        message: `Delete keyphrase(s):<b> ${rowsToDelete
          .map((keyPhrase) =>
            [keyPhrase.key_phrase_gr, keyPhrase.key_phrase_en].join(', ')
          )
          .join(' </b>and <b>')}</b>?
        <br><b severity='danger'>Save changes to persist.</b>`,
        header: `${rowsToDelete.length} rows will be deleted.`,
        accept: () => {
          this.deletedRows.update((current) => [
            ...current,
            ...rowsToDelete.map((keyphrase) => keyphrase.key_phrase_gr!)
          ]);
  
          this.addedRows.update((rows) =>
            rows.filter(row => !this.selectedKeyPhrases()
              .map(k => k.key_phrase_gr)
              .includes(row))
          );
  
          this.keyphraseData = this.keyphrasextractionTableIncoming().filter(
            (val) => !this.selectedKeyPhrases().includes(val)
          );
  
          this._stateService.updateKeyphrasesOnDelete({
            keyphraseExtractionTableIncoming: this.keyphraseData,
          });
  
          this.selectedKeyPhrases.update(() => []);
          
          if (rowsToDelete.length > 0) {
            this._messageService.add({
              severity: 'success',
              summary: 'Successful',
              detail: 'Key Phrases Deleted',
              life: 2000,
            });
          }
        }
      });
    } else {
      this.addedRows.update((rows) =>
        rows.filter(row => !this.selectedKeyPhrases()
          .map(k => k.key_phrase_gr)
          .includes(row))
      );
  
      this.keyphraseData = this.keyphrasextractionTableIncoming().filter(
        (val) => !this.selectedKeyPhrases().includes(val)
      );
  
      this._stateService.updateKeyphrasesOnDelete({
        keyphraseExtractionTableIncoming: this.keyphraseData,
      });
  
      this.selectedKeyPhrases.update(() => []);
    }
  }
  

  /**
   * @summary Initializes row editing mode.
   * @description Creates a clone of the row being edited to allow for
   * cancellation and restoration of original values.
   *
   * @param {KeyphraseRow} keyphraseRow The row to be edited
   */
  onRowEditInit(keyphraseRow: KeyphraseRow) {
    this.cloneKeyphraseRow[keyphraseRow.key_phrase_gr as string] = {
      ...keyphraseRow,
    };
  }

  /**
   * @summary Saves changes made to a row in edit mode.
   * @description Validates the edited row, updates change tracking,
   * and shows appropriate success/error messages.
   *
   * @param {KeyphraseRow} keyphraseRow The row being saved
   *
   * @see rowChanged() for change tracking
   */
  onRowEditSave(keyphraseRow: KeyphraseRow) {
    
    if (keyphraseRow.key_phrase_gr && keyphraseRow.key_area && keyphraseRow.key_area_gr_definition && keyphraseRow.key_phrase_en) {
      this.rowChanged(keyphraseRow);
      delete this.cloneKeyphraseRow[keyphraseRow.key_phrase_gr];
      this._messageService.add({
        severity: 'warn',
        summary: 'Success',
        detail: 'Key Phrase was updated. Save changes to persist.',
      });
    } else {
      this._messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid edit',
      });
    }
  }

  /**
   * @summary Cancels row editing and restores original values.
   * @description Reverts changes made to a row by restoring its
   * original values from the clone created at edit initialization.
   *
   * @param {KeyphraseRow} keyphraseRow The row being cancelled
   * @param {number} index The index of the row in the table
   */
  onRowEditCancel(keyphraseRow: KeyphraseRow, index: number) {
    const originalRow = this.cloneKeyphraseRow[keyphraseRow.key_phrase_gr!];
    
    keyphraseRow.key_area = originalRow.key_area!;
    keyphraseRow.key_area_gr_definition = originalRow.key_area_gr_definition!;
    keyphraseRow.key_area_en_definition = originalRow.key_area_en_definition!;
    keyphraseRow.key_phrase_gr = originalRow.key_phrase_gr!;
    keyphraseRow.key_phrase_en = originalRow.key_phrase_en!;
    
    this.changedRows.update(rows => 
      rows.filter(row => row !== keyphraseRow.key_phrase_gr)
    );
    
    delete this.cloneKeyphraseRow[keyphraseRow.key_phrase_gr!];
  }

  /**
   * @summary Checks if a row has pending changes.
   * @description Determines whether a specific row has been modified
   * and is pending save, to highlight it appropriately in the UI.
   *
   * @param {string} keyPhraseGr The key_phrase_gr of the row to check
   * @returns {boolean} True if the row has been changed, false otherwise
   */
  isRowChanged(keyPhraseGr: string): boolean {
    return this.changedRows().includes(keyPhraseGr);
  }

  /**
   * @summary Checks if a row was newly added.
   * @description Determines whether a specific row was added in the
   * current session and is pending save, to highlight it appropriately in the UI.
   *
   * @param {string} keyPhraseGr The key_phrase_gr of the row to check
   * @returns {boolean} True if the row was newly added, false otherwise
   */
  isRowAdded(keyPhraseGr: string): boolean {
    return this.addedRows().includes(keyPhraseGr);
  }

  // Private Methods
  private rowChanged(keyphraseRow: KeyphraseRow) {
    if (!this.changedRows().includes(keyphraseRow.key_phrase_gr!)) {
      this.changedRows.update(rows => [...rows, keyphraseRow.key_phrase_gr!]);
    }
  }
}









